import React, { createContext, useState, useContext, useCallback, ReactNode, useEffect } from "react";
import { User } from "../services/authService";
import { getAuthService } from "../services/factory";

interface AuthContextValue {
	user: User | null;
	isAuthenticated: boolean;
	authLoading: boolean;
	login: (user: User) => void;
	logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [user, setUser] = useState<User | null>(null);
	const [authLoading, setAuthLoading] = useState<boolean>(true);

	// 🔹 bootstrap session on app start
	useEffect(() => {
		const bootstrapAuth = async () => {
			try {
				const authService = getAuthService();
				const user = await authService.getCurrentUser();

				if (user) {
					setUser(user);
				}
			} catch (err) {
				console.error("Auth bootstrap failed", err);
			} finally {
				setAuthLoading(false);
			}
		};

		bootstrapAuth();
	}, []);

	const login = useCallback((user: User) => {
		setUser(user);
	}, []);

	const logout = useCallback(async () => {
		try {
			const authService = getAuthService();
			await authService.logout();
		} catch (err) {
			console.error("Logout error", err);
		} finally {
			setUser(null);
		}
	}, []);

	const value: AuthContextValue = {
		user,
		isAuthenticated: user !== null,
		authLoading,
		login,
		logout,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
